'use client'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronsLeftIcon } from 'lucide-react'
import Image from 'next/image'
import React, { type PropsWithChildren } from 'react'

import { cn } from '@/lib/cn'

type SidebarContextValue = {
  isCollapsed: boolean
}
const SidebarContext = React.createContext({
  isCollapsed: false,
} as SidebarContextValue)

const sidebarVariants = cva(
  'flex h-screen w-full max-w-[268px] flex-col bg-system-neutral-white transition-all duration-500 ease-in-out',
  {
    variants: {
      isCollapsed: {
        true: 'max-w-[76px]',
        false: 'w-[268px] max-w-[268px]',
      },
    },
    defaultVariants: {
      isCollapsed: false,
    },
  },
)
type SidebarProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof sidebarVariants>
const Sidebar: React.FC<PropsWithChildren<SidebarProps>> = ({
  isCollapsed = false,
  className,
  ...props
}) => {
  const collapsed = typeof isCollapsed === 'boolean' ? isCollapsed : false
  return (
    <SidebarContext.Provider
      value={{
        isCollapsed: collapsed,
      }}
    >
      <aside
        className={sidebarVariants({ isCollapsed, className })}
        {...props}
      />
    </SidebarContext.Provider>
  )
}
Sidebar.displayName = 'Sidebar'

const useSidebar = () => {
  const sidebarContext = React.useContext(SidebarContext)

  if (!sidebarContext) {
    throw new Error('useSidebar should be used within <Sidebar>')
  }

  return sidebarContext
}

type SidebarHeaderProps = {
  logoUrl: string
  altText: string
  isLoadingLogoUrl?: boolean
  onCollapse: () => void
}
const SidebarHeader: React.FC<SidebarHeaderProps> = ({
  logoUrl,
  altText,
  isLoadingLogoUrl,
  onCollapse,
}) => {
  const { isCollapsed } = useSidebar()

  return (
    <div className="relative flex min-h-20 w-full items-center justify-center py-3">
      <button
        type="button"
        onClick={onCollapse}
        className={cn(
          'absolute flex h-7 w-5 cursor-pointer items-center justify-center rounded-l-md border-y border-l border-system-grey-support bg-system-background-base-contrast transition-transform duration-500',
          isCollapsed ? '-right-5 rotate-180' : 'right-0',
        )}
      >
        <ChevronsLeftIcon size={16} className="text-system-text-secondary" />
      </button>
      {!isLoadingLogoUrl && (
        <Image
          src={logoUrl}
          alt={altText}
          width={100}
          height={100}
          className="size-auto max-h-12 object-cover"
        />
      )}
    </div>
  )
}
SidebarHeader.displayName = 'SidebarHeader'

const SidebarContent: React.FC<PropsWithChildren> = ({ ...props }) => {
  return <div className="flex h-full flex-col px-3 py-4" {...props} />
}
SidebarContent.displayName = 'SidebarContent'

const sidebarListVariants = cva('flex flex-col', {
  variants: {
    isSubList: {
      true: 'gap-3',
      false: 'gap-4',
    },
  },
  defaultVariants: {
    isSubList: false,
  },
})
type SidebarListProps = React.HTMLAttributes<HTMLUListElement> &
  VariantProps<typeof sidebarListVariants>
const SidebarList: React.FC<PropsWithChildren<SidebarListProps>> = ({
  isSubList,
  className,
  ...props
}) => {
  const { isCollapsed } = useSidebar()
  if (isCollapsed && isSubList) return null

  return (
    <ul
      className={cn(
        sidebarListVariants({ isSubList, className }),
        isCollapsed ? 'items-center' : '',
      )}
      {...props}
    />
  )
}
SidebarList.displayName = 'SidebarList'

const sidebarItemVariants = cva(
  'relative flex cursor-pointer items-center gap-2 rounded-lg p-1 text-sm font-normal md:p-2 md:text-base',
  {
    variants: {
      mode: {
        filled: '',
        outlined: '',
      },
      isActive: {
        true: '',
        false: 'text-system-text-primary',
      },
      isSubItem: {
        true: 'ml-8 before:absolute before:-left-3 before:h-[calc(100%+12px)] before:w-px before:bg-system-grey-support after:absolute after:-left-3 after:h-px after:w-3 after:bg-system-grey-support last:before:top-[-6px] last:before:h-[calc(50%+6px)]',
        false: 'ml-0',
      },
      isCollapsed: {
        true: '',
        false: '',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50',
        false: '',
      },
    },
    compoundVariants: [
      {
        isActive: true,
        mode: 'filled',
        className: 'bg-system-primary-purple text-white',
      },
      {
        isActive: true,
        mode: 'outlined',
        className: 'bg-inherited text-system-primary-purple',
      },
      {
        isCollapsed: true,
        isActive: true,
        className:
          'cursor-normal pointer-events-none size-10 justify-center gap-0 rounded-lg bg-system-primary-purple text-white',
      },
    ],
    defaultVariants: {
      isActive: false,
      isSubItem: false,
      isCollapsed: false,
      mode: 'filled',
    },
  },
)
type SidebarItemProps = React.LiHTMLAttributes<HTMLLIElement> &
  VariantProps<typeof sidebarItemVariants> & {
    asChild?: boolean
  }
const SidebarItem = React.forwardRef<
  HTMLLIElement,
  PropsWithChildren<SidebarItemProps>
>(
  (
    {
      mode,
      isActive,
      isSubItem,
      className,
      disabled,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const { isCollapsed } = useSidebar()
    if (isCollapsed && isSubItem) return null

    const Comp = asChild ? Slot : 'li'

    return (
      <Comp
        ref={ref}
        className={cn(
          sidebarItemVariants({
            mode,
            isActive,
            isSubItem,
            isCollapsed,
            disabled,
            className,
          }),
        )}
        {...props}
      />
    )
  },
)
SidebarItem.displayName = 'SidebarItem'

type SidebarFooterProps = {
  logoUrl: string
  altText: string
}
const SidebarFooter: React.FC<SidebarFooterProps> = ({ logoUrl, altText }) => {
  return (
    <div className="mt-auto flex items-center justify-center border-t border-system-grey-support p-5 transition-all duration-500 ease-in-out">
      <Image
        src={logoUrl}
        alt={altText}
        className="size-auto min-h-4 object-cover"
        width={100}
        height={100}
      />
    </div>
  )
}
SidebarFooter.displayName = 'SidebarFooter'

export {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarList,
  SidebarItem,
  SidebarFooter,
}
